import { useState } from 'react';
import { Input } from 'antd';
import Modal from '../../../components/Modal';
import toastMsg from '../../../lib/toastMsg';
import * as Styled from './style';

export type PickerMode = 'single' | 'multi';

export type PickerModalProps<TItem> = {
  open: boolean;
  title: string;
  searchPlaceholder: string;
  emptyText: string;
  mode: PickerMode;
  items: TItem[];
  getId: (item: TItem) => number;
  getLabel: (item: TItem) => string;
  onSearch: (keyword: string) => void;
  onClose: () => void;
  onConfirm: (selected: TItem[]) => void;
};

/**
 * 쿠폰/기획전 선택 공통 모달 (검색 + 단일/다중 선택).
 * 데이터 페칭은 래퍼(CouponPickerModal/EventPickerModal)가 담당하고, 선택 state/UI는 여기서 관리한다.
 * 선택 항목을 스냅샷으로 보관하므로 검색어를 바꿔도 선택이 유지된다.
 */
export const PickerModal = <TItem,>(props: PickerModalProps<TItem>) => {
  const { open, title, searchPlaceholder, emptyText, mode, items, getId, getLabel, onSearch, onClose, onConfirm } =
    props;
  // Map 사용 — 숫자 키 Record는 id 오름차순으로 재정렬돼 클릭(선택) 순서가 사라진다.
  // 만료 유형은 선택 순서 = 우선순위 초기값이므로 삽입 순서 보존이 필요.
  const [selected, setSelected] = useState<Map<number, TItem>>(new Map());

  const toggle = (item: TItem): void => {
    const id = getId(item);
    setSelected((prev) => {
      if (mode === 'single') {
        return new Map([[id, item]]);
      }
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, item);
      }
      return next;
    });
  };

  // 컴포넌트가 항상 마운트 상태라 destroyOnClose로는 선택/검색이 안 지워짐 → 직접 초기화
  const reset = (): void => {
    setSelected(new Map());
    onSearch('');
  };

  const handleConfirm = (): void => {
    // 빈 선택으로 닫히면 무동작인데 피드백이 없어 누락을 눈치채기 어려움 — 모달 유지 + 안내
    if (selected.size === 0) {
      toastMsg.error('항목을 선택해주세요.');
      return;
    }
    onConfirm([...selected.values()]);
    reset();
    onClose();
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={open}
      title={title}
      width={520}
      isFooter
      okText="선택 완료"
      onOk={handleConfirm}
      onClose={handleClose}
      destroyOnClose
    >
      <Styled.PickerSearchRow>
        <Input.Search placeholder={searchPlaceholder} allowClear onSearch={onSearch} />
      </Styled.PickerSearchRow>
      <Styled.PickerList>
        {items.map((item) => {
          const id = getId(item);
          return (
            <Styled.PickerRow key={id}>
              <input
                type={mode === 'single' ? 'radio' : 'checkbox'}
                name="alimtalk-picker"
                checked={selected.has(id)}
                onChange={() => toggle(item)}
              />
              <Styled.SelectedName>{getLabel(item)}</Styled.SelectedName>
              <Styled.IdBadge>#{id}</Styled.IdBadge>
            </Styled.PickerRow>
          );
        })}
        {items.length === 0 && <Styled.EmptyText>{emptyText}</Styled.EmptyText>}
      </Styled.PickerList>
    </Modal>
  );
};
